import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { absoluteAssetUrl, api, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { SceneMenu } from "./SceneMenu";

// Plays only this scene, cut from the latest finished video (fetched as a blob so auth applies).
export function ScenePreviewDialog({ scene, moduleId, onClose, onChanged }) {
    const [info, setInfo] = useState(null);
    const [src, setSrc] = useState(null);
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState("");
    useEffect(() => {
        if (!scene) return undefined;
        let url = null;
        setInfo(null); setSrc(null); setEditing(false);
        api.get(`/modules/${moduleId}/scenes/${scene.id}/preview-info`).then(async (r) => {
            setInfo(r.data);
            setText(r.data.narration || "");
            if (r.data.available) {
                const b = await api.get(`/modules/${moduleId}/scenes/${scene.id}/preview`, { responseType: "blob" });
                url = URL.createObjectURL(b.data);
                setSrc(url);
            }
        }).catch((e) => toast.error(formatApiError(e)));
        return () => { if (url) URL.revokeObjectURL(url); };
    }, [scene, moduleId]);

    const saveText = async () => {
        try {
            const field = scene.trainer_dialogue ? "trainer_dialogue" : "voiceover";
            await api.patch(`/scenes/${scene.id}`, { [field]: text });
            await api.post(`/scenes/${scene.id}/unapprove`).catch(() => null);
            toast.success(`Scene ${scene.scene_number}: text saved, back to Needs review`, { duration: 8000 });
            setEditing(false);
            onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <Dialog open={!!scene} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="scene-preview-dialog" className="max-w-3xl">
                <DialogHeader>
                    <div className="flex items-center justify-between gap-3 pr-6">
                        <DialogTitle>Scene {scene?.scene_number} preview</DialogTitle>
                        {scene && <SceneMenu scene={scene} moduleId={moduleId} onChanged={onChanged} />}
                    </div>
                    <DialogDescription>Only this scene, with its voice and captions.</DialogDescription>
                </DialogHeader>
                <div className="aspect-video w-full overflow-hidden rounded-md bg-neutral-900" data-testid="scene-preview-media">
                    {!info ? (
                        <div className="flex h-full items-center justify-center text-neutral-400"><Loader2 className="animate-spin" size={20} /></div>
                    ) : info.available ? (
                        src ? <video src={src} controls autoPlay className="h-full w-full" data-testid="scene-preview-video" />
                            : <div className="flex h-full items-center justify-center text-sm text-neutral-400"><Loader2 className="mr-2 animate-spin" size={16} />Preparing preview…</div>
                    ) : (
                        <div className="relative h-full w-full">
                            {info.still_url && <img src={absoluteAssetUrl(info.still_url)} alt="" className="h-full w-full object-contain" data-testid="scene-preview-still" />}
                            <div data-testid="scene-preview-note" className="absolute inset-x-0 bottom-0 bg-black/60 px-3 py-2 text-center text-xs text-white">{info.note}</div>
                        </div>
                    )}
                </div>
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] uppercase tracking-widest text-neutral-400">Narration</span>
                        {!editing && <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="scene-preview-edit-text">Edit text</Button>}
                    </div>
                    {editing ? (
                        <>
                            <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} data-testid="scene-preview-text-input" />
                            <div className="flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => setEditing(false)} data-testid="scene-preview-text-cancel">Cancel</Button>
                                <Button size="sm" onClick={saveText} className="bg-neutral-900 text-white" data-testid="scene-preview-text-save">Save</Button>
                            </div>
                        </>
                    ) : <p data-testid="scene-preview-narration" className="text-sm text-neutral-700">{info?.narration}</p>}
                </div>
            </DialogContent>
        </Dialog>
    );
}
